import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  RequestForQuotationInboxDto,
  UpdateRequestForQuotationRequest,
  UploadRequestForQuotationResponse,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'

interface IRequestForQuotationInboxClient {
  list(): Promise<RequestForQuotationInboxDto[]>
  detail(rfqId: string): Promise<RequestForQuotationInboxDto>
  upload(file: File, correlationId?: string): Promise<UploadRequestForQuotationResponse>
  download(rfqId: string): Promise<boolean>
  setBuyerRelation(
    rfqId: string,
    request: UpdateRequestForQuotationRequest,
  ): Promise<void>
  createProject(rfqId: string): Promise<void>
}

export class RequestForQuotationInboxClient
  extends InternalAPI
  implements IRequestForQuotationInboxClient
{
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(
      `/api/parties/${partyId}/request-for-quotations/inbox`,
      onRequestChange,
    )
  }

  public list(): Promise<RequestForQuotationInboxDto[]> {
    return this.GetAsync({ relativePath: '', id: 'ListRfqInbox' })
  }

  public detail(rfqId: string): Promise<RequestForQuotationInboxDto> {
    return this.GetAsync({
      relativePath: `/${rfqId}`,
      id: `GetRfqInbox-${rfqId}`,
    })
  }

  public upload(file: File, correlationId?: string): Promise<UploadRequestForQuotationResponse> {
    const formData = new FormData()
    formData.append('file', file)
    if (correlationId) formData.append('correlationId', correlationId)

    return this.PostAuthenticatedAsync({
      relativePath: '/upload',
      data: formData,
      id: 'UploadRfqInbox',
    })
  }

  public download(rfqId: string) {
    return this.DownloadAsync({
      relativePath: `/${rfqId}/document`,
      id: `DownloadRfqInbox-${rfqId}`,
    })
  }

  public setBuyerRelation(
    rfqId: string,
    request: UpdateRequestForQuotationRequest,
  ): Promise<void> {
    return this.PutAuthenticatedAsync({
      relativePath: `/${rfqId}`,
      data: request,
      id: `SetRfqInboxBuyerRelation-${rfqId}`,
    })
  }

  public createProject(rfqId: string): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${rfqId}/create-project`,
      id: `CreateProjectFromRfqInbox-${rfqId}`,
    })
  }
}

export class RequestForQuotationInboxController extends BaseController<IRequestForQuotationInboxClient> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new RequestForQuotationInboxClient(partyId, onRequestChange)
    })
  }

  public async list(): Promise<RequestForQuotationInboxDto[]> {
    try {
      return await this.api.list()
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async detail(rfqId: string): Promise<RequestForQuotationInboxDto> {
    try {
      return await this.api.detail(rfqId)
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async upload(file: File, correlationId?: string): Promise<UploadRequestForQuotationResponse> {
    try {
      return await this.api.upload(file, correlationId)
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async download(rfqId: string) {
    try {
      return await this.api.download(rfqId)
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async setBuyerRelation(
    rfqId: string,
    buyerRelationId: string | undefined,
  ): Promise<void> {
    try {
      await this.api.setBuyerRelation(rfqId, { buyerRelationId })
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async createProject(rfqId: string): Promise<void> {
    try {
      await this.api.createProject(rfqId)
    } catch (error) {
      throw this.HandleError(error)
    }
  }
}
